import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

const ThemeContext = createContext(null);
const STORAGE_KEY = "appTheme";

/** "system" follows the OS; "dark"/"light" pin the app to one theme. */
function readStoredPreference() {
  const stored = localStorage.getItem(STORAGE_KEY);
  return stored === "dark" || stored === "light" || stored === "system" ? stored : "dark";
}

function systemTheme() {
  return window.matchMedia?.("(prefers-color-scheme: light)").matches ? "light" : "dark";
}

export function ThemeProvider({ children }) {
  const [preference, setPreference] = useState(readStoredPreference);
  const [resolved, setResolved] = useState(() =>
    (readStoredPreference() === "system" ? systemTheme() : readStoredPreference()));

  // Paint the resolved theme onto <html> — every colour token hangs off it.
  useEffect(() => {
    const apply = () => {
      const next = preference === "system" ? systemTheme() : preference;
      setResolved(next);
      document.documentElement.setAttribute("data-theme", next);
      document.documentElement.style.colorScheme = next;
      // Keep the mobile browser chrome in step with the app surface.
      document.querySelector('meta[name="theme-color"]')
        ?.setAttribute("content", next === "light" ? "#f3f6fb" : "#080d17");
    };
    apply();
    localStorage.setItem(STORAGE_KEY, preference);

    if (preference !== "system") return undefined;
    const mq = window.matchMedia("(prefers-color-scheme: light)");
    mq.addEventListener("change", apply);
    return () => mq.removeEventListener("change", apply);
  }, [preference]);

  const toggleTheme = useCallback(() => {
    setPreference((current) => {
      const active = current === "system" ? systemTheme() : current;
      return active === "dark" ? "light" : "dark";
    });
  }, []);

  const value = useMemo(
    () => ({ theme: resolved, preference, setTheme: setPreference, toggleTheme, isDark: resolved === "dark" }),
    [resolved, preference, toggleTheme],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used within a ThemeProvider");
  return ctx;
}
